import React, { useEffect, useMemo, useState } from 'react';
import { useBreadcrumbs, useUser } from '../hooks';
import { Icon, Header, Label, Button, Modal, Table, SegmentControl, Field, Message, Toast, Toolbar, Checkbox, Input } from 'react-frontier';
import { EventDateList, EventStatusType, SetLoading, Location, UserClass } from '../SiteClasses';
import { useSearchParams } from 'react-router-dom';
import { addCommas, bindClick, bindClose } from '../SiteUtil';
import { firstBy } from 'thenby';
import { Autocomplete, EventCard, SearchUserModal } from '../components';
import { Statistic } from 'semantic-ui-react';
import Calendar from '../components/Calendar';
import API from '../API';
import moment from 'moment';
import Validator from '../Validator';
import classNames from 'classnames';

enum Modals{
	ONBOARD = 1,
	COORDINATOR = 2,
	EXECUTIVE = 3,
}

enum Control{
	ALL_EVENTS = 1,
	MY_EVENTS = 2,
	COORDINATOR_EVENTS = 3,
	EXECUTIVE_EVENTS = 4,
}

enum Filters{
	CLOSED = 1,
	NOT_OPERATED = 2,
	PROMOTER_OPERATED = 3,
	POSTPONED = 4,
	CANCELLED = 5,
}

var Dashboard = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { user } = useUser();
	var [searchParams, setSearchParams] = useSearchParams();
	var [dateRange, setDateRange] = useState<{ start: Date, end: Date }>(()=>{
		var ds = searchParams.get('ds');
		var de = searchParams.get('de');
		if(ds && de){
			var start = moment(ds, 'YYYY-MM-DD').toDate();
			var end = moment(de, 'YYYY-MM-DD').toDate();
			if(!isNaN(start.getTime()) && !isNaN(end.getTime())){
				return { start, end };
			}
		}
		return { start: new Date(), end: new Date() };
	});
	var [loading, setLoading] = useState<boolean>(false);
	var [events, setEvents] = useState<EventDateList[]>([]);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [calendarLoading, setCalendarLoading] = useState<boolean>(false);
	var [calendarError, setCalendarError] = useState<boolean>(false);
	var [showingDate, setShowingDate] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [onboardingStatus, setOnboardingStatus] = useState<EventStatusType>(null);
	var [eventLocation, setEventLocation] = useState<Location>(null);
	var [hasParentEvent, setHasParentEvent] = useState<boolean>(false);
	var [parentDateId, setParentDateId] = useState<string>('');
	var [control, setControl] = useState<Control>(()=>{
		var c = searchParams.get('c');
		return c ? parseInt(c, 10) as Control : Control.ALL_EVENTS;
	});
	var [selectedCoordinator, setSelectedCoordinator] = useState<UserClass>(()=>{
		var cid = searchParams.get('cid');
		var cn = searchParams.get('cn');
		return (cid && cn) ? { user_id: parseInt(cid, 10), name: cn } as UserClass : null;
	});
	var [selectedExecutive, setSelectedExecutive] = useState<UserClass>(()=>{
		var eid = searchParams.get('eid');
		var en = searchParams.get('en');
		return (eid && en) ? { user_id: parseInt(eid, 10), name: en } as UserClass : null;
	});
	var [filters, setFilters] = useState<Filters[]>([]);
	var [monthEvents, setMonthEvents] = useState<{
		year: number,
		month: number
		days: {
			day: number,
			events: number,
			pending: number,
		}[]
	}[]>([]);
	var [currentViewingMonth, setCurrentViewingMonth] = useState<{ month: number, year: number }>(null);
	var [statusStats, setStatusStats] = useState<{ total: number, operating: number, promoter: number, not_operated: number, postponed: number, cancelled: number, closed: number, unconfigured: number }>(null);

	var get = ()=>{
		var coordinator_id = control === Control.MY_EVENTS ? user?.user_id :
			control === Control.COORDINATOR_EVENTS ? selectedCoordinator?.user_id :
			null;
		var executive_id = control === Control.EXECUTIVE_EVENTS ? selectedExecutive?.user_id : null;

		var date_start = moment(dateRange.start).format('YYYY-MM-DD') as any;
		var date_end = moment(dateRange.end).format('YYYY-MM-DD') as any;

		setLoading(true);
		API.getEvents(null, null, date_start, date_end, coordinator_id, executive_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			return setEvents(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener los eventos. (GEL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	};

	var viewingMonthChange = (month: number, year: number)=>{
		setCurrentViewingMonth({ month, year });

		var prev_month = { month: month-1, year };
		if(prev_month.month<=0){
			prev_month.month = 12;
			prev_month.year -= 1;
		}
		var next_month = { month: month+1, year };
		if(next_month.month>12){
			next_month.month = 1;
			next_month.year += 1;
		}
		var months = [
			prev_month,
			{ month, year },
			next_month
		]

		setCalendarLoading(true);
		setCalendarError(false);

		var coordinator_id = control === Control.MY_EVENTS ? user?.user_id :
			control === Control.COORDINATOR_EVENTS ? selectedCoordinator?.user_id :
			null;
		var executive_id = control === Control.EXECUTIVE_EVENTS ? selectedExecutive?.user_id : null;

		API.getMonthEvents(months, coordinator_id, executive_id).then(res=>{
			if(res.error){
				setCalendarError(true);
				return;
			}

			var evm : typeof monthEvents = [];
			for(var i of res.data){
				var mx = evm.findIndex(a=>a.month===i.month && a.year===i.year);
				if(mx==-1){
					evm.push({
						year: i.year,
						month: i.month,
						days: [{
							day: i.day,
							events: i.events,
							pending: i.pending,
						}]
					});
				}else{
					evm[mx].days.push({
						day: i.day,
						events: i.events,
						pending: i.pending,
					});
				}
			}
			setMonthEvents(evm);
		}).catch(err=>{
			setCalendarError(true);
		}).finally(()=>{
			setCalendarLoading(false);
		})

		API.getMonthStatusStats(month, year, coordinator_id, executive_id).then(res=>{
			if(res.error) return;
			setStatusStats(res.data);
		}).catch(()=>{});
	}

	useEffect(()=>{
		setTitle('Eventos');
		get();
	}, [dateRange, control, selectedCoordinator, selectedExecutive, user]);

	useEffect(()=>{
		if(currentViewingMonth){
			viewingMonthChange(currentViewingMonth.month, currentViewingMonth.year);
		}
	}, [control, selectedCoordinator, selectedExecutive, user]);

	useEffect(()=>{
		var urlParams: Record<string, string> = {
			ds: moment(dateRange.start).format('YYYY-MM-DD'),
			de: moment(dateRange.end).format('YYYY-MM-DD'),
		};

		if(control !== Control.ALL_EVENTS) urlParams.c = control.toString();
		if(selectedCoordinator){
			urlParams.cid = selectedCoordinator.user_id.toString();
			urlParams.cn = selectedCoordinator.name;
		}
		if(selectedExecutive){
			urlParams.eid = selectedExecutive.user_id.toString();
			urlParams.en = selectedExecutive.name;
		}

		setSearchParams(urlParams, { replace: true });
	}, [dateRange, control, selectedCoordinator, selectedExecutive]);

	var showOnboardModal = (date_id: number)=>{
		return ()=>{
			setShowingDate(date_id);
			setOnboardingStatus(EventStatusType.OPERATING);
			setHasParentEvent(false);
			setParentDateId('');
			setShownModal(Modals.ONBOARD);
		}
	}

	var sendOnboarding = async(setLoading: SetLoading)=>{
		var alreadyHasLocation = onboardingStatus === EventStatusType.OPERATING && !!shown_event?.location_id;
		var { valid, prompts } = Validator({ date_id: showingDate, status: onboardingStatus, eventLocation, parentDateId: hasParentEvent ? parentDateId : null }, {
			date_id: [{
				rule: 'number', prompt: 'Favor de seleccionar el evento a configurar',
			}],
			status: [{
				rule: 'number', prompt: 'Favor de seleccionar el estatus del evento',
			}],
			...(onboardingStatus === EventStatusType.OPERATING && !alreadyHasLocation ? {
				eventLocation: [{
					rule: 'empty', label: 'Ubicación'
				}],
			} : {}),
			...(hasParentEvent ? {
				parentDateId: [{
					rule: 'number', prompt: 'Favor de ingresar el evento padre',
				}],
			} : {}),
		})
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.updateEventStatus(showingDate, onboardingStatus, onboardingStatus === EventStatusType.OPERATING && !alreadyHasLocation ? eventLocation : null, hasParentEvent ? parseInt(parentDateId) : null).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var ev = [...events];
			var ex = ev.findIndex(a=>a.date_id===showingDate);
			if(ex!==-1){
				ev[ex].status_id = onboardingStatus;
				ev[ex].location_id = res.data?.location_id || shown_event?.location_id || null;
			}
			setEvents(ev);
			setEventLocation(null);
			setHasParentEvent(false);
			setParentDateId('');
			Toast.success('Se ha configurado el evento');
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error configurando el evento (LCL-DSSNONB-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var event_groups = useMemo(()=>{
		var groups : {
			date: number,
			events: EventDateList[],
		}[] = [];
		if(!events || events.length==0) return groups;

		for(var e of events){
			if(e.parent_date_id){
				var pdx = events.findIndex(a=>a.date_id===e.parent_date_id);
				if(pdx!==-1){
					events[pdx].tickets_sold += e.tickets_sold;
					events[pdx].tickets_free += e.tickets_free;
				}
			}
		}
		var filteredEvents = events.filter(e => !e.parent_date_id);

		for(var i of filteredEvents){
			var dt = moment.unix(i.date).startOf('day').unix();
			var gix = groups.findIndex(a=>a.date==dt);
			if(gix==-1){
				groups.push({
					date: dt,
					events: [i],
				});
			}else{
				groups[gix].events.push(i);
			}
		}
		for(var g of groups){
			g.events = g.events.sort(
				firstBy('tickets_sold', 'desc')
				.thenBy('date', 'asc')
				.thenBy('date_id')
			)
		}
		return groups.sort(firstBy('date', 'asc'));
	}, [events, dateRange]);

	var hasVisibleEvents = useMemo(()=>{
		if(!event_groups || event_groups.length === 0) return false;

		for(var g of event_groups){
			var visibleInGroup = g.events.filter(e => {
				if(filters.includes(Filters.CLOSED) && e.closed) return false;
				if(!filters.includes(Filters.NOT_OPERATED) && e.status_id === EventStatusType.NOT_OPERATED) return false;
				if(!filters.includes(Filters.PROMOTER_OPERATED) && e.status_id === EventStatusType.PROMOTER_OPERATED && !e.user_coordinator) return false;
				if(!filters.includes(Filters.POSTPONED) && e.status_id === EventStatusType.POSTPONED) return false;
				if(!filters.includes(Filters.CANCELLED) && e.status_id === EventStatusType.CANCELLED) return false;
				return true;
			});
			if(visibleInGroup.length > 0) return true;
		}
		return false;
	}, [event_groups, filters]);

	var day_labels = useMemo(()=>{
		if(!monthEvents) return [];
		var all_days = monthEvents.reduce((a,b)=>[...a, ...b.days.map(a=>({
			month: b.month,
			year: b.year,
			day: a.day,
			label: a.events,
			iconName: a.pending==0 ? null : 'exclamation-triangle',
			className: a.pending>0 ? 'orange' : 'green'
		}))], []);
		return all_days;
	}, [monthEvents]);

	var shown_event = useMemo(()=>{
		if(!showingDate || !events) return null;
		return events.find(a=>a.date_id===showingDate)
	}, [showingDate, events]);

	var closedEventsCount = useMemo(()=>{
		if(!events) return 0;
		return events.filter(e => e.closed && !e.parent_date_id).length;
	}, [events]);

	var notOperatedEventsCount = useMemo(()=>{
		if(!events) return 0;
		return events.filter(e => e.status_id === EventStatusType.NOT_OPERATED && !e.parent_date_id).length;
	}, [events]);

	var promoterOperatedEventsCount = useMemo(()=>{
		if(!events) return 0;
		return events.filter(e => e.status_id === EventStatusType.PROMOTER_OPERATED && !e.parent_date_id && !e.user_coordinator).length;
	}, [events]);

	var postponedEventsCount = useMemo(()=>{
		if(!events) return 0;
		return events.filter(e => e.status_id === EventStatusType.POSTPONED && !e.parent_date_id).length;
	}, [events]);

	var cancelledEventsCount = useMemo(()=>{
		if(!events) return 0;
		return events.filter(e => e.status_id === EventStatusType.CANCELLED && !e.parent_date_id).length;
	}, [events]);

	var onFilter = (filter: Filters)=>{
		return ()=>{
			if(filters.includes(filter)){
				setFilters(filters.filter(f => f !== filter));
			}else{
				setFilters([...filters, filter]);
			}
		}
	}

	var onControlChange = (value: Control)=>{
		setControl(value);
		if(value === Control.COORDINATOR_EVENTS){
			setShownModal(Modals.COORDINATOR);
		}else if(value === Control.EXECUTIVE_EVENTS){
			setShownModal(Modals.EXECUTIVE);
		}else if(value === Control.ALL_EVENTS){
			setSelectedCoordinator(null);
			setSelectedExecutive(null);
		}
	}

	var onCoordinatorSelected = (coordinator: UserClass)=>{
		setSelectedCoordinator(coordinator);
		setShownModal(null);
	}

	var onExecutiveSelected = (executive: UserClass)=>{
		setSelectedExecutive(executive);
		setShownModal(null);
	}

	var eventStatistics = useMemo(()=>{
		if(!currentViewingMonth || !monthEvents?.length) return { daily: 0, weekly: 0, monthly: 0 };

		var viewedMonthData = monthEvents.find(m => m.month === currentViewingMonth.month && m.year === currentViewingMonth.year);
		if(!viewedMonthData) return { daily: 0, weekly: 0, monthly: 0 };

		var monthly = viewedMonthData.days.reduce((sum, d) => sum + d.events, 0);

		var daysInMonth = moment({ year: currentViewingMonth.year, month: currentViewingMonth.month - 1 }).daysInMonth();
		var daily = daysInMonth ? Math.round(monthly / daysInMonth) : 0;

		var weeksInMonth = new Set<string>();
		for(var d = 1; d <= daysInMonth; d++){
			weeksInMonth.add(moment({ year: currentViewingMonth.year, month: currentViewingMonth.month - 1, day: d }).format('gggg-ww'));
		}
		var weekly = weeksInMonth.size ? Math.round(monthly / weeksInMonth.size) : 0;

		return { daily, weekly, monthly };
	}, [monthEvents, currentViewingMonth]);

	if(loadError) return <Header text='Error' subtext={loadError} style={{ marginTop: 25 }} />

	return <div className='ar dashboard-screen'>
		<div className="left">
			<Calendar
				startDate={dateRange?.start}
				endDate={dateRange?.end}
				dayLabels={day_labels}
				emptyLabel='0'
				loading={calendarLoading}
				onViewingMonthChange={viewingMonthChange}
				onDateChange={(start, end)=>{
					setDateRange({ start, end });
				}}
			/>
			{calendarError && (
				<Message type='error' style={{ marginTop: 15 }}>
					Hubo un error al cargar los eventos del calendario
				</Message>
			)}
			<Field label='Filtros' style={{ marginTop: 15 }}>
				<SegmentControl
					segments={[
						{ text: 'Todos', value: Control.ALL_EVENTS },
						{ text: 'Mis eventos', value: Control.MY_EVENTS },
						{ text: 'Coordinador', value: Control.COORDINATOR_EVENTS },
						{ text: 'Comercial', value: Control.EXECUTIVE_EVENTS },
					]}
					color='blue'
					onClick={onControlChange}
					selected={[control]}
				/>
			</Field>
			{control === Control.COORDINATOR_EVENTS && selectedCoordinator && (
				<div style={{ margin: 10, marginTop: 0 }}>
					<Message style={{ textAlign: 'center', fontSize: 16, marginTop: 15, padding: 15 }}>
						Mostrando eventos de <b>{selectedCoordinator.name}</b>
					</Message>
				</div>
			)}
			{control === Control.EXECUTIVE_EVENTS && selectedExecutive && (
				<div style={{ margin: 10, marginTop: 0 }}>
					<Message style={{ textAlign: 'center', fontSize: 16, marginTop: 15, padding: 15 }}>
						Mostrando eventos de <b>{selectedExecutive.name}</b>
					</Message>
				</div>
			)}
			<div className='stats-container'>
				<Statistic.Group widths='three' size='mini'>
					<Statistic>
						<Statistic.Value>{eventStatistics.daily}</Statistic.Value>
						<Statistic.Label>Día</Statistic.Label>
					</Statistic>
					<Statistic>
						<Statistic.Value>{eventStatistics.weekly}</Statistic.Value>
						<Statistic.Label>Semana</Statistic.Label>
					</Statistic>
					<Statistic>
						<Statistic.Value>{eventStatistics.monthly}</Statistic.Value>
						<Statistic.Label>Mes</Statistic.Label>
					</Statistic>
				</Statistic.Group>
			</div>
			<div className='stats-container' style={{ marginTop: 10, marginBottom: 10 }}>
				<Statistic.Group widths='three' size='mini'>
					<Statistic>
						<Statistic.Value>{statusStats?.total ?? '-'}</Statistic.Value>
						<Statistic.Label>Total</Statistic.Label>
					</Statistic>
					<Statistic color='green'>
						<Statistic.Value>{statusStats?.operating ?? '-'}</Statistic.Value>
						<Statistic.Label>AREMA</Statistic.Label>
					</Statistic>
					<Statistic color='olive'>
						<Statistic.Value>{statusStats?.promoter ?? '-'}</Statistic.Value>
						<Statistic.Label>Promotor</Statistic.Label>
					</Statistic>
				</Statistic.Group>
				<Statistic.Group widths='three' size='mini' style={{ marginTop: 8 }}>
					<Statistic color='grey'>
						<Statistic.Value>{statusStats?.not_operated ?? '-'}</Statistic.Value>
						<Statistic.Label>No operado</Statistic.Label>
					</Statistic>
					<Statistic color='yellow'>
						<Statistic.Value>{statusStats?.postponed ?? '-'}</Statistic.Value>
						<Statistic.Label>Pospuesto</Statistic.Label>
					</Statistic>
					<Statistic color='red'>
						<Statistic.Value>{statusStats?.cancelled ?? '-'}</Statistic.Value>
						<Statistic.Label>Cancelado</Statistic.Label>
					</Statistic>
				</Statistic.Group>
				<Statistic.Group widths='one' size='mini' style={{ marginTop: 8 }}>
					<Statistic color='blue'>
						<Statistic.Value>{statusStats?.unconfigured ?? '-'}</Statistic.Value>
						<Statistic.Label>Sin configurar</Statistic.Label>
					</Statistic>
				</Statistic.Group>
			</div>
		</div>
		<div className="right">
			<div>
				<Toolbar style={{ borderRadius: 0, boxShadow: 'none', borderLeft: '0', borderTop: '0', borderRight: '0' }}>
					<Toolbar.Item style={{ fontSize: 20, fontWeight: 'bold', color: 'black' }} disabled>
						Eventos
					</Toolbar.Item>
					<Toolbar.Dropdown
                  iconName='filter'
                  text='Filtrar'
                  iconRight={filters.length > 0 ? 'asterisk' : null}
						items={[
							{
								iconName: 'trash',
								text: 'Vaciar filtros',
								onClick: bindClick(setFilters, [])
							},
							{
								text: (
									<>
										<i className={classNames('icon circle', {
											'check': filters.includes(Filters.CLOSED),
											'outline': !filters.includes(Filters.CLOSED),
										})}></i>
										Ocultar cerrados ({closedEventsCount})
									</>
								),
								onClick: onFilter(Filters.CLOSED)
							},
							{
								text: (
									<>
										<i className={classNames('icon circle', {
											'check': filters.includes(Filters.NOT_OPERATED),
											'outline': !filters.includes(Filters.NOT_OPERATED),
										})}></i>
										Mostrar no operados ({notOperatedEventsCount})
									</>
								),
								onClick: onFilter(Filters.NOT_OPERATED)
							},
							{
								text: (
									<>
										<i className={classNames('icon circle', {
											'check': filters.includes(Filters.PROMOTER_OPERATED),
											'outline': !filters.includes(Filters.PROMOTER_OPERATED),
										})}></i>
										Mostrar operados por promotor ({promoterOperatedEventsCount})
									</>
								),
								onClick: onFilter(Filters.PROMOTER_OPERATED)
							},
							{
								text: (
									<>
										<i className={classNames('icon circle', {
											'check': filters.includes(Filters.POSTPONED),
											'outline': !filters.includes(Filters.POSTPONED),
										})}></i>
										Mostrar pospuestos ({postponedEventsCount})
									</>
								),
								onClick: onFilter(Filters.POSTPONED)
							},
							{
								text: (
									<>
										<i className={classNames('icon circle', {
											'check': filters.includes(Filters.CANCELLED),
											'outline': !filters.includes(Filters.CANCELLED),
										})}></i>
										Mostrar cancelados ({cancelledEventsCount})
									</>
								),
								onClick: onFilter(Filters.CANCELLED)
							}
						]}
					/>
				</Toolbar>
			</div>
			<div className="events">
				{loading ? (
					<Header loading text={'Cargando datos'} />
				) : events && events.length !== 0 ? (
					hasVisibleEvents ? (
						<>
							{event_groups.map((g, index)=>{
								if(g.events.length==0) return null;
								return (
									<React.Fragment key={`DATE-${g.date}`}>
										<div className="header">
											<Header className='date' text={moment.unix(g.date).format('DD/MMM')} size='small' centered={false}/>
										</div>
										{g.events.filter(e => {
											if(e.parent_date_id) return false;
											if(filters.includes(Filters.CLOSED) && e.closed) return false;
											if(!filters.includes(Filters.NOT_OPERATED) && e.status_id === EventStatusType.NOT_OPERATED) return false;
											if(!filters.includes(Filters.PROMOTER_OPERATED) && e.status_id === EventStatusType.PROMOTER_OPERATED && !e.user_coordinator) return false;
											if(!filters.includes(Filters.POSTPONED) && e.status_id === EventStatusType.POSTPONED) return false;
											if(!filters.includes(Filters.CANCELLED) && e.status_id === EventStatusType.CANCELLED) return false;
											return true;
										}).sort(firstBy('date')).map(e => (
											<EventCard
												key={`EVI-${e.date_id}`}
												event={e}
												onClick={showOnboardModal(e.date_id)}
											/>
										))}
								</React.Fragment>
							)
						})}
						</>
					) : (
						<div className="empty" style={{ textAlign: 'center', marginTop: 15, fontSize: 16, color: 'gray' }}>
							No hay eventos en las fechas seleccionadas.
						</div>
					)
				) : (
					<div className="empty" style={{ textAlign: 'center', marginTop: 15, fontSize: 16, color: 'gray' }}>
						No hay eventos en las fechas seleccionadas.
					</div>
				)}
			</div>
		</div>
		<Modal open={shownModal===Modals.ONBOARD} onClose={bindClose(setShownModal)} size='tiny'>
			<Modal.Header>Configurar evento</Modal.Header>
			{!!shown_event && (
				<Modal.Content>
					<Header text={'Configurar evento'} subtext={shown_event.event_name} />
					<Table details striped
						style={{ marginBottom: 15, marginTop: 20 }}
						titleSize='small'
						title='Información'
						data={[
							['Calendario', shown_event.date_id],
							['Nombre', shown_event.event_name],
							['Fecha', moment.unix(shown_event.date).format('DD/MMMM/YYYY HH:mm')],
							null,
							['Promotor', shown_event.promoter_name],
							['Ciudad', shown_event.city],
							['Boletos', shown_event.tickets_sold===null ? <Icon name='minus' color='gray' /> : addCommas(shown_event.tickets_sold, false)],
							['Cortesías', shown_event.tickets_free===null ? <Icon name='minus' color='gray' /> : addCommas(shown_event.tickets_free, false)],
							['Recinto', shown_event.venue_name || <Icon name='minus'/>]
						]}
					/>
					<Field label='Estatus'>
						<SegmentControl
							color='blue'
							segments={[
								{ text: 'Operado por AREMA', value: EventStatusType.OPERATING },
								{ text: 'Operado por Promotor', value: EventStatusType.PROMOTER_OPERATED },
								{ text: 'No operado', value: EventStatusType.NOT_OPERATED }
							]}
							onClick={setOnboardingStatus}
							selected={onboardingStatus ? [onboardingStatus] : []}
						/>
					</Field>
					{onboardingStatus===EventStatusType.NOT_OPERATED ? (
						<Message type='info'>
							El evento se configurará como <b>NO OPERADO</b> y será escondido de la lista de eventos próximos
						</Message>
					) : onboardingStatus===EventStatusType.PROMOTER_OPERATED ? (
						<Message type='info'>
							El evento se configurará como <b>OPERADO POR PROMOTOR</b>
						</Message>
					) : shown_event.location_id ? (
						<Message type='info'>
							Este evento ya cuenta con una ubicación asignada.
						</Message>
					) : <Field label='Ubicación' comment='Ubicación del recinto'>
						<Autocomplete
							placeholder='Buscar recinto o lugar público...'
							onPlaceSelected={(l) => setEventLocation(l)}
							types={['establishment']}
						/>
						</Field>}
						<div style={{ marginTop: 15 }}>
							<Checkbox
								label='Relacionar con evento padre'
								checked={hasParentEvent}
								onChange={setHasParentEvent}
							/>
						</div>
						{hasParentEvent && (
							<Field label='ID calendario' comment='Ingresa el ID calendario del evento padre' style={{ marginTop: 15 }}>
								<Input
									placeholder='ID calendario'
									value={parentDateId}
									onChange={setParentDateId}
								/>
							</Field>
						)}
					<Message list={modalPrompts} type='error' />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClose(setShownModal)} />
				<Button text='Configurar' color='black' onClick={sendOnboarding} />
			</Modal.Actions>
		</Modal>
		<SearchUserModal
			onClose={()=>{
				setShownModal(null);
				if(!selectedCoordinator){
					setControl(Control.ALL_EVENTS);
				}
			}}
			onSelected={onCoordinatorSelected}
			open={shownModal === Modals.COORDINATOR}
		/>
		<SearchUserModal
			onClose={()=>{
				setShownModal(null);
				if(!selectedExecutive){
					setControl(Control.ALL_EVENTS);
				}
			}}
			onSelected={onExecutiveSelected}
			open={shownModal === Modals.EXECUTIVE}
		/>
	</div>
}

export default Dashboard;
